<template>
  <div
    class="p-[12px_16px] flex-center h-[47px] text-font-1"
    :style="!successInfo ? 'border-bottom: 1px solid var(--back-4)' : ''"
  >
    <b v-if="!successInfo">{{ $t('dui-huan-quan-yi') }}</b>
    <CloseOutlined :class="CloseIconClass" @click="cancel" />
  </div>
  <div v-if="!successInfo" class="p-[36px_50px] h-[326px] box-border">
    <View class="voucher-form" />
  </div>
  <div
    v-else
    class="p-[37px_90px_0] min-h-[326px] box-border flex items-center flex-col"
  >
    <Resource :src="SuccessIcon" class="w-[57px] h-[57px]" />
    <b class="text-[20px] m-[13px_0_6px] leading-[1]">
      {{ $t('dui-huan-cheng-gong') }}
    </b>
    <span class="text-font-60 text-14 text-center">
      {{ $t('nin-yi-cheng-gong-dui-huan-quan-yi') }}
    </span>
    <div class="w-full text-16 p-[6px_20px] bg-back-3 rounded-[8px] mt-[20px]">
      <div v-if="successInfo?.coin_days > 0" class="flex-between py-[14px]">
        <b class="flex-center">
          <iconpark-icon name="yanbi" size="18" class="mr-[4px]" />
          {{ successInfo?.coins }}{{ $t('yan-bi') }}
        </b>
        <span class="text-font-60">
          {{
            $t('you-xiao-qi-successinfocoindays-tian', [successInfo?.coin_days])
          }}
        </span>
      </div>
      <div v-if="successInfo?.rights_days > 0" class="flex-between py-[14px]">
        <b class="flex-center">
          <Resource
            class="w-[18px] h-[18px] mr-[4px]"
            :src="getFeUrl('/youyan-platform/png/package.png')"
            :transform-resource="transformImage"
          />
          {{ successInfo?.rights_name }}
        </b>
        <span class="text-font-60">
          {{
            $t('you-xiao-qi-successinfocoindays-tian', [
              successInfo?.rights_days,
            ])
          }}
        </span>
      </div>
    </div>
    <XAttract
      class="mt-[26px] mb-[50px]"
      :source="AttractSource.EXCHANGE"
    ></XAttract>
  </div>
  <div
    v-if="!successInfo"
    class="p-[16px] flex-center gap-x-[16px]"
    style="border-top: 1px solid var(--back-4)"
  >
    <template v-if="!successInfo">
      <a-button class="w-[90px]" type="primary" ghost @click="cancel">
        {{ $t('qu-xiao') }}
      </a-button>
      <a-button
        class="w-[90px]"
        type="primary"
        :loading="loading"
        @click="submit"
      >
        {{ $t('dui-huan') }}
      </a-button>
    </template>
    <a-button v-else class="w-[90px]" type="primary" @click="cancel">
      {{ $t('zhi-dao-le') }}
    </a-button>
  </div>
</template>

<script lang="ts" setup>
import type { Rule } from 'ant-design-vue/es/form'
import { AttractSource } from '~/components/xAttract/constant'
import XAttract from '~/components/xAttract/index.vue'
import { useForm } from '~/features/form'
import { checkSmsCode } from '~/api'
import {
  CloseIconClass,
  CloseOutlined,
} from '~/hooks/useModal/modals/closeIcon'
import { useTime } from '~/hooks/useTime'
const { $t } = useNuxtApp()
const { timeFormat } = useTime()

const props = defineProps<{
  success?: () => void
  cancel: () => void
}>()
const SuccessIcon = transformImage(getFeUrl('/youyan-platform/png/success.png'))

const validatorCode = (_rule: Rule, value: string) => {
  if (!value) {
    return Promise.reject($t('qing-shu-ru-dui-huan-ma'))
  }

  if (!/^[A-Za-z0-9]{12,16}$/.test(value)) {
    return Promise.reject($t('ge-shi-cuo-wu'))
  } else {
    return Promise.resolve()
  }
}

const { View, validate, modelRef } = useForm(
  [
    {
      key: 'code',
      inputAttrs: {
        type: 'input',

        onBlur: () => {
          if (modelRef.code) {
            validate()
          }
        },
      },
      formItem: {
        label: $t('dui-huan-ma'),
        name: 'code',
        rules: [{ validator: validatorCode, trigger: 'blur' }],
      },
    },
  ],
  {
    labelCol: { span: 24 },
    wrapperCol: { span: 24 },
  }
)

const loading = ref(false)
const successInfo = ref()

const submit = async () => {
  try {
    await validate()
    loading.value = true
    try {
      const res = await checkSmsCode({ code: modelRef.code })
      loading.value = false
      successInfo.value = res.data
      trackEvent(TRACK_EVENT.YOUYAN_PLATFORM_EXCHANGECODE_SUBMIT, {
        PayTime: timeFormat(new Date()),
        ExchangeSuc: 1,
      })
      if (!res.data) {
        return
      }

      trackEvent(TRACK_EVENT.USER_EXCHANGE_CODE_SUCCESS)

      props?.success?.()
      const {
        activity_name,
        activity_id,
        exchange_time,
        coins,
        coin_days,
        rights_days,
        rights_name,
      } = res.data
      const base = {
        ExchangeTime: timeFormat(exchange_time),
        CampaignName: activity_name,
        CampaignID: activity_id,
        ExchangeCode: modelRef.code,
      }
      if (coin_days > 0) {
        trackEvent(TRACK_EVENT.YOUYAN_PLATFORM_EXCHANGECODE_SUBMIT_TOKEN, {
          ...base,
          TokenNum: coins,
          ValidityPeriod: coin_days,
        })
      }
      if (rights_days > 0) {
        trackEvent(TRACK_EVENT.YOUYAN_PLATFORM_EXCHANGECODE_SUBMIT_TOKEN, {
          ...base,
          ExcPackage: rights_name,
          ValidityPeriod: rights_days,
        })
      }
      await useUpdateUserAndAccounts()
    } catch (error) {
      console.error(error)

      loading.value = false
      trackEvent(TRACK_EVENT.YOUYAN_PLATFORM_EXCHANGECODE_SUBMIT, {
        PayTime: timeFormat(new Date()),
        ExchangeSuc: 2,
      })
    }
  } catch (error) {
    console.error(error)
  }
}

onMounted(() => {
  trackEvent(TRACK_EVENT.YOUYAN_PLATFORM_REDEMPTIONCODE_ENTRY)
})
</script>

<style lang="scss" scoped>
.voucher-form {
  :deep(.ant-form-item-label) {
    label {
      @apply text-font-80 font-bold h-[20px];
      &::before {
        content: none;
      }
    }
  }
  :deep(.ant-input) {
    @apply py-[6px];
  }
}
</style>
